import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Loader2, LogOut, Mail, KeyRound, Save, FileUp, FileCheck2,
  Send, MessageSquare, ShieldCheck,
} from "lucide-react";
import { candidateApi, api, formatApiErrorDetail } from "@/lib/api";
import { IMAGES } from "@/lib/constants";

const STATUS_LABELS = {
  preinscrit: { label: "En attente", cls: "text-neutral-300 border-white/20" },
  retenu: { label: "Retenu", cls: "text-theatre-gold border-theatre-gold/50" },
  ecarte: { label: "Écarté", cls: "text-neutral-400 border-white/20" },
  refuse: { label: "Dossier non présenté", cls: "text-theatre-red border-theatre-red/50" },
};
const fieldCls = "bg-black/50 border-white/15 text-white focus-visible:ring-theatre-gold focus-visible:border-theatre-gold rounded-none h-12";
const areaCls = "bg-black/50 border-white/15 text-white focus-visible:ring-theatre-gold focus-visible:border-theatre-gold rounded-none";

export default function EspaceCandidat() {
  const [checking, setChecking] = useState(true);
  const [me, setMe] = useState(null);

  const load = useCallback(async () => {
    if (!localStorage.getItem("va_cand_token")) { setChecking(false); return; }
    try {
      const { data } = await candidateApi.get("/candidate/me");
      setMe(data);
    } catch {
      localStorage.removeItem("va_cand_token");
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theatre-ink" data-testid="candidate-loading">
        <Loader2 className="animate-spin text-theatre-gold" size={32} />
      </div>
    );
  }

  return me
    ? <Dashboard me={me} setMe={setMe} onLogout={() => { localStorage.removeItem("va_cand_token"); setMe(null); }} />
    : <Login onSuccess={(data) => setMe(data)} />;
}

function Login({ onSuccess }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await candidateApi.post("/candidate/request-code", { email });
      toast.success("Si un compte existe, un code vous a été envoyé par email.");
      setStep(2);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await candidateApi.post("/candidate/verify-code", { email, code });
      localStorage.setItem("va_cand_token", data.access_token);
      toast.success("Bienvenue dans votre espace candidat.");
      onSuccess(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="grain min-h-screen flex items-center justify-center px-6 bg-theatre-ink" data-testid="candidate-login-page">
      <div className="absolute inset-0 spotlight-glow" aria-hidden="true" />
      <div className="relative w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-theatre-gold transition-colors duration-200 mb-8">
          <ArrowLeft size={14} /> Retour au site
        </Link>
        <div className="bg-black/60 backdrop-blur-xl border border-theatre-gold/20 p-10">
          <div className="text-center mb-8">
            <img src={IMAGES.logo} alt="Viva Artist" className="h-14 w-14 object-contain mx-auto" />
            <h1 className="mt-4 font-serif text-2xl text-white">Espace Candidat</h1>
            <p className="mt-2 text-sm text-neutral-500">Connexion par code à usage unique</p>
          </div>

          {step === 1 ? (
            <form onSubmit={requestCode} className="space-y-5" data-testid="candidate-email-form">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">Email de votre candidature</Label>
                <Input data-testid="candidate-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={fieldCls} />
              </div>
              <button type="submit" data-testid="candidate-request-code" disabled={loading} className="w-full bg-theatre-red hover:bg-theatre-redhover disabled:opacity-60 text-white py-3.5 text-xs uppercase tracking-[0.25em] transition-colors duration-300 flex items-center justify-center gap-2">
                {loading ? <Loader2 className="animate-spin" size={16} /> : <Mail size={16} />}
                Recevoir mon code
              </button>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-5" data-testid="candidate-code-form">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">Code reçu par email</Label>
                <Input data-testid="candidate-code" required value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} placeholder="000000" className={`${fieldCls} tracking-[0.5em] text-center text-lg`} />
              </div>
              <button type="submit" data-testid="candidate-verify-code" disabled={loading} className="w-full bg-theatre-red hover:bg-theatre-redhover disabled:opacity-60 text-white py-3.5 text-xs uppercase tracking-[0.25em] transition-colors duration-300 flex items-center justify-center gap-2">
                {loading ? <Loader2 className="animate-spin" size={16} /> : <KeyRound size={16} />}
                Se connecter
              </button>
              <button type="button" onClick={() => setStep(1)} className="w-full text-xs uppercase tracking-[0.2em] text-neutral-500 hover:text-theatre-gold">
                Modifier l'email
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Dashboard({ me, setMe, onLogout }) {
  const open = me.preinscription_open;
  const [form, setForm] = useState({
    prenom: me.prenom, nom: me.nom, telephone: me.telephone, parti: me.parti,
    profession: me.profession, region: me.region, vision: me.vision, programme: me.programme,
  });
  const [saving, setSaving] = useState(false);
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState("");
  const [dossierName, setDossierName] = useState(me.dossier_filename);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const st = STATUS_LABELS[me.status] || STATUS_LABELS.preinscrit;

  const loadMessages = useCallback(() => {
    candidateApi.get("/candidate/messages").then(({ data }) => setMessages(data)).catch(() => {});
  }, []);
  useEffect(() => { loadMessages(); }, [loadMessages]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await candidateApi.patch("/candidate/me", form);
      setMe({ ...data, preinscription_open: open });
      toast.success("Candidature mise à jour.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const replaceDossier = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf") { toast.error("Le dossier doit être un PDF."); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Fichier trop volumineux (10 Mo max)."); return; }
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/dossier/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await candidateApi.patch("/candidate/me", { dossier_file_id: data.file_id, dossier_filename: data.filename });
      setDossierName(data.filename);
      toast.success("Dossier mis à jour.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    try {
      const { data } = await candidateApi.post("/candidate/messages", { text: msg });
      setMessages((m) => [...m, data]);
      setMsg("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="grain min-h-screen bg-theatre-ink px-6 lg:px-10 py-8" data-testid="candidate-dashboard">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <img src={IMAGES.logo} alt="Viva Artist" className="h-10 w-10 object-contain" />
            <span className="font-serif text-lg text-white">Espace Candidat</span>
          </div>
          <button data-testid="candidate-logout" onClick={onLogout} className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-theatre-red transition-colors duration-200">
            <LogOut size={14} /> Déconnexion
          </button>
        </div>

        <div className="bg-black/50 border border-theatre-gold/20 p-6 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="candidate-status-card">
          <div>
            <p className="font-serif text-2xl text-white">{me.prenom} {me.nom}</p>
            <p className="text-sm text-neutral-500 mt-1">{me.email}</p>
          </div>
          <span className={`self-start sm:self-auto border px-4 py-2 text-xs uppercase tracking-[0.2em] ${st.cls}`} data-testid="candidate-status">
            {st.label}
          </span>
        </div>

        {!open && (
          <div className="bg-theatre-red/10 border border-theatre-red/30 text-neutral-300 text-sm px-5 py-4 mb-8 flex items-center gap-2">
            <ShieldCheck size={16} className="text-theatre-red" /> Les pré-inscriptions sont closes : votre candidature n'est plus modifiable.
          </div>
        )}

        {/* Edit form */}
        <div className="bg-black/40 border border-white/10 p-6 md:p-8 mb-8">
          <h2 className="font-serif text-xl text-theatre-gold mb-6">Ma candidature</h2>
          <fieldset disabled={!open} className="space-y-5 disabled:opacity-60">
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">Prénom</Label><Input data-testid="cand-prenom" value={form.prenom} onChange={set("prenom")} className={fieldCls} /></div>
              <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">Nom</Label><Input data-testid="cand-nom" value={form.nom} onChange={set("nom")} className={fieldCls} /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">Téléphone</Label><Input data-testid="cand-telephone" value={form.telephone} onChange={set("telephone")} className={fieldCls} /></div>
              <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">Parti / Mouvement</Label><Input data-testid="cand-parti" value={form.parti} onChange={set("parti")} className={fieldCls} /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">Profession</Label><Input data-testid="cand-profession" value={form.profession} onChange={set("profession")} className={fieldCls} /></div>
              <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">Région</Label><Input data-testid="cand-region" value={form.region} onChange={set("region")} className={fieldCls} /></div>
            </div>
            <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">Vision</Label><Textarea data-testid="cand-vision" rows={4} value={form.vision} onChange={set("vision")} className={areaCls} /></div>
            <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">Programme</Label><Textarea data-testid="cand-programme" rows={3} value={form.programme} onChange={set("programme")} className={areaCls} /></div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">Dossier (PDF)</Label>
              <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={replaceDossier} data-testid="cand-dossier-input" />
              <div className="flex items-center gap-3">
                {dossierName ? (
                  <span className="flex items-center gap-2 text-sm text-theatre-gold"><FileCheck2 size={16} /> {dossierName}</span>
                ) : (
                  <span className="text-sm text-neutral-600">Aucun dossier</span>
                )}
                <button type="button" data-testid="cand-dossier-btn" onClick={() => fileRef.current?.click()} className="flex items-center gap-2 border border-white/20 hover:border-theatre-gold text-neutral-300 hover:text-theatre-gold px-3 py-2 text-xs uppercase tracking-[0.15em] transition-colors duration-200">
                  <FileUp size={14} /> {dossierName ? "Remplacer" : "Ajouter"}
                </button>
              </div>
            </div>
          </fieldset>

          {open && (
            <button data-testid="cand-save" onClick={save} disabled={saving} className="mt-6 bg-theatre-red hover:bg-theatre-redhover disabled:opacity-60 text-white px-8 py-3 text-xs uppercase tracking-[0.25em] transition-colors duration-300 flex items-center gap-2">
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Enregistrer
            </button>
          )}
        </div>

        {/* Messaging */}
        <div className="bg-black/40 border border-white/10 p-6 md:p-8" data-testid="candidate-messaging">
          <h2 className="font-serif text-xl text-theatre-gold mb-6 flex items-center gap-2"><MessageSquare size={18} /> Messagerie avec l'organisation</h2>
          <div className="space-y-3 max-h-80 overflow-y-auto custom-scroll mb-5 pr-2">
            {messages.length === 0 && <p className="text-sm text-neutral-600 italic">Aucun message pour le moment.</p>}
            {messages.map((m) => (
              <div key={m.id} data-testid={`cand-msg-${m.id}`} className={`max-w-[80%] p-3 text-sm ${m.sender === "candidate" ? "ml-auto bg-theatre-red/20 border border-theatre-red/30 text-neutral-100" : "bg-white/5 border border-white/10 text-neutral-300"}`}>
                <p className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 mb-1">{m.sender === "candidate" ? "Vous" : "Viva Artist"}</p>
                {m.text}
              </div>
            ))}
          </div>
          <form onSubmit={sendMessage} className="flex gap-3">
            <Input data-testid="cand-msg-input" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Votre message…" className={fieldCls} />
            <button type="submit" data-testid="cand-msg-send" className="bg-theatre-red hover:bg-theatre-redhover text-white px-5 transition-colors duration-200 flex items-center"><Send size={16} /></button>
          </form>
        </div>
      </div>
    </div>
  );
}
